import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    Search,
    Sparkles,
} from "lucide-react";
import { Logo } from "../components/shared/Logo";
import { Button } from "../components/shared/Button";
import { Footer } from "../components/shared/Footer";
import { ShareEarn } from "../components/glimpse/ShareEarn";
import { getShareStatus, lookupShareByEmail } from "../lib/api";

export default function Status() {
    const [params] = useSearchParams();
    const pathParams = useParams();
    const navigate = useNavigate();
    const initialCode =
        (pathParams.code || params.get("code") || "").trim().toUpperCase();

    const [code, setCode] = useState(initialCode);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(Boolean(initialCode));
    const [notFound, setNotFound] = useState(false);
    const [email, setEmail] = useState("");
    const [emailErr, setEmailErr] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);

    useEffect(() => {
        if (!code) return;
        let alive = true;
        setLoading(true);
        setNotFound(false);
        getShareStatus(code).then((res) => {
            if (!alive) return;
            if (res.ok) {
                setStatus(res.data);
            } else {
                setStatus(null);
                setNotFound(true);
            }
            setLoading(false);
        });
        return () => {
            alive = false;
        };
    }, [code]);

    async function onLookup(e) {
        e.preventDefault();
        setEmailErr(null);
        setEmailLoading(true);
        const res = await lookupShareByEmail(email.trim());
        setEmailLoading(false);
        if (res.ok) {
            navigate(`/r/${res.data.share_code}`);
        } else if (res.rateLimited) {
            setEmailErr("Too many attempts. Try again in an hour.");
        } else {
            setEmailErr("We couldn't find that email in our records.");
        }
    }

    return (
        <div className="relative min-h-screen klareet-grain klareet-glow">
            <header className="relative z-10 px-5 md:px-12 pt-6 pb-2 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <Logo className="h-14 md:h-16 w-auto" />
                </Link>
                <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                    Referral status
                </div>
            </header>

            <main className="relative z-10 px-5 md:px-12 pt-10 pb-24 max-w-3xl mx-auto">
                {loading ? (
                    <div className="grid place-items-center py-24">
                        <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
                    </div>
                ) : status ? (
                    <div className="space-y-6">
                        {status.free_year_earned ? (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="rounded-3xl border border-klareet-GREEN/50 bg-gradient-to-br from-klareet-GREEN/10 to-klareet-T/5 p-8"
                                data-testid="status-earned"
                            >
                                <div className="flex items-center gap-3">
                                    <Sparkles className="text-klareet-GREEN" size={28} />
                                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-klareet-TEXT">
                                        You've earned 1 year of Klareet free
                                    </h1>
                                </div>
                                <p className="mt-3 text-klareet-MUT leading-relaxed">
                                    Come back when we launch in Q3 2026 and
                                    we'll have it waiting. {status.email_on_file
                                        ? "We'll email you at launch."
                                        : "Add your email on this page so we can reach you."}
                                </p>
                            </motion.div>
                        ) : (
                            <div className="rounded-3xl border border-klareet-bor bg-klareet-card p-8">
                                <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                                    Referral code
                                </div>
                                <h1 className="mt-2 text-3xl md:text-4xl font-mono font-semibold text-klareet-TEXT">
                                    {status.share_code}
                                </h1>
                                <p className="mt-3 text-klareet-MUT">
                                    You're building towards 1 year of Klareet
                                    free. Keep sharing.
                                </p>
                            </div>
                        )}

                        <ShareEarn
                            shareCode={status.share_code}
                            responseId={status.response_id}
                            initialEmailOnFile={status.email_on_file}
                            variant="status"
                        />

                        <div>
                            <Button variant="outline" onClick={() => navigate("/")}>
                                <ArrowLeft size={16} />
                                Back to home
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="rounded-3xl border border-klareet-bor bg-klareet-card p-8">
                            <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                                {notFound
                                    ? "Code not found"
                                    : "Check your referral progress"}
                            </div>
                            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-klareet-TEXT">
                                {notFound
                                    ? "We couldn't find that code."
                                    : "Enter your referral code"}
                            </h1>
                            <p className="mt-3 text-klareet-MUT leading-relaxed">
                                Your code looks like <span className="font-mono text-klareet-TEXT">KLRXXXXX</span>. If you can't find it, we can look it up with the email you used.
                            </p>

                            <div className="mt-6 flex flex-col md:flex-row gap-2">
                                <input
                                    value={code}
                                    onChange={(e) =>
                                        setCode(e.target.value.toUpperCase())
                                    }
                                    placeholder="KLRXXXXX"
                                    className="flex-1 px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-card font-mono text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40"
                                    data-testid="status-code-input"
                                />
                                <Button
                                    onClick={() =>
                                        navigate(`/r/${code}`)
                                    }
                                    disabled={!code}
                                    data-testid="status-code-submit"
                                >
                                    <Search size={16} />
                                    Check
                                </Button>
                            </div>
                        </div>

                        <form
                            onSubmit={onLookup}
                            className="rounded-3xl border border-klareet-bor bg-klareet-card p-8"
                            data-testid="email-lookup-form"
                        >
                            <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                                Forgot your code?
                            </div>
                            <h2 className="mt-2 text-xl md:text-2xl font-semibold tracking-tight text-klareet-TEXT">
                                Look it up with your email
                            </h2>
                            <div className="mt-4 flex flex-col md:flex-row gap-2">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-card text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40"
                                    data-testid="lookup-email-input"
                                />
                                <Button
                                    size="md"
                                    onClick={onLookup}
                                    disabled={emailLoading}
                                    data-testid="lookup-email-submit"
                                >
                                    {emailLoading ? "Looking…" : "Find my code"}
                                </Button>
                            </div>
                            {emailErr ? (
                                <div className="mt-3 text-sm text-klareet-RED font-mono">
                                    {emailErr}
                                </div>
                            ) : null}
                        </form>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}
